import './ParentContainerV2.css';
import React, { useEffect, useState, useRef } from 'react';
import CCPContainer from '../CCPContainer';
import config from '../../../utils/env-config/env-config.json';
import InfoTab from '../InfoTab';
import NotLoggedIn from '../NotLoggedIn';
import remoteConfigManager from '../../../utils/remote-config-manager';
import webApisClient from '../../../utils/web-apis-client';
import { identify, event } from 'react-fullstory';
import 'amazon-connect-streams';
import 'amazon-connect-chatjs';
import 'aws-sdk/clients/connectparticipant'; // <-- should be after streams, chatjs to avoid AWS namespace conflicts https://github.com/amazon-connect/amazon-connect-chatjs#using-the-aws-sdk-and-chatjs

/* global connect */

const ParentContainerV2 = () => {
  const [loggedIn, setLoggedIn] = useState(true);
  const [activeContactId, setActiveContactId] = useState('');
  const [hasContacts, setHasContacts] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [attributesMap, setAttributesMap] = useState({});
  const [ccpWidth, setCCPWidth] = useState(95);
  const [ccpInitialized, setCcpInitialized] = useState(false);
  const [surveyCalls, setSurveyCalls] = useState({});
  const [surveyChats, setSurveyChats] = useState({});
  const [surveyCallbackTask, setSurveyCallbackTask] = useState({});
  const [surveyFailureTask, setSurveyFailureTask] = useState({});
  const [surveyIntermittentAbsenceTask, setSurveyIntermittentAbsenceTask] =
    useState({});
  const [surveyRequestTypeOptions, setSurveyRequestTypeOptions] = useState([]);
  const [surveyWorkItemTypeOptions, setSurveyWorkItemTypeOptions] = useState(
    [],
  );
  const [surveyCallerTypeOptions, setSurveyCallerTypeOptions] = useState([]);
  const [infoTabSelected, setInfoTabSelected] = useState(0);
  const [surveyCaseIds, setSurveyCaseIds] = useState([]);
  const [showVerintIdentifier, setShowVerintIdentifier] = useState(false);
  const [surveyResponses, setSurveyResponses] = useState([]);

  // Refs for data that must be accessible inside stale closures (event handlers registered once)
  const activeContactRef = useRef('');
  const enrichedAttributesRef = useRef(new Map());
  const acceptedContactIdsRef = useRef(new Set());
  const borderColorIndexRef = useRef(0);
  const surveyCaseIdsRef = useRef(surveyCaseIds);

  const envConfig = config[globalThis.location.hostname];
  const contactBorderColors = ['#62892E', '#0199A6', '#735F91'];
  const userRef = useRef(remoteConfigManager.getUser());

  // Keep surveyCaseIdsRef in sync with state
  useEffect(() => {
    surveyCaseIdsRef.current = surveyCaseIds;
  }, [surveyCaseIds]);

  // Sync activeContactRef with state for use in event handler closures
  useEffect(() => {
    activeContactRef.current = activeContactId;
  }, [activeContactId]);

  // --- Helpers ---

  /** Safely get agent's current contacts from the Connect API */
  const getAgentContacts = () => {
    try {
      return new connect.Agent().getContacts();
    } catch {
      console.error('Error getting agent contacts from Connect API');
      return [];
    }
  };

  /** Check if a contact is a monitoring session (supervisor silent monitor or barge) */
  const isMonitoringContact = (contact) => {
    try {
      return !!contact.getAgentConnection()?.getMonitorStatus();
    } catch {
      return false;
    }
  };

  /** Get the interaction type string for a Connect contact */
  const getInteractionType = (contact) => {
    try {
      if (
        contact.getType() === 'voice' ||
        contact.getType() === 'queue_callback'
      ) {
        return contact.getConnections()?.[1]?.getEndpoint()?.phoneNumber || 'Voice';
      } else if (contact.getType() === 'task') {
        return 'Task';
      } else {
        return 'Chat';
      }
    } catch {
      // getConnections() can throw internally for certain contact types (e.g. callbacks)
      const type = contact.getType();
      if (type === 'task') return 'Task';
      if (type === 'chat') return 'Chat';
      return 'Voice';
    }
  };

  const formatDate = (date) => {
    let formattedDate = '';
    if (date.includes('T')) {
      const d = date.split('T')[0];
      formattedDate = d.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$2-$3-$1');
      return formattedDate;
    } else if (!date.includes('T') && !date.includes('-')) {
      if (date.length === 8 && /^\d{8}$/.test(date)) {
        formattedDate = date.replace(/(\d{2})(\d{2})(\d{4})/g, '$1-$2-$3');
        return formattedDate;
      }
      formattedDate = date.replace(/(\d{1,2})(\d{1,2})(\d{4})/g, '$1-$2-$3');
      return formattedDate;
    } else {
      return date;
    }
  };

  /** Enrich a contact's DOB — decrypt for voice, format for others. Writes to enrichedAttributesRef and updates attributesMap. */
  const enrichDob = (contactId, contactType, dobValue) => {
    if (!dobValue || dobValue === '') return;

    // Skip if already enriched for this contact
    const existing = enrichedAttributesRef.current.get(contactId);
    if (existing?.plainTextDob !== undefined) return;

    if (contactType === connect.ContactType.VOICE) {
      // Set empty placeholder immediately, decrypt async
      enrichedAttributesRef.current.set(contactId, {
        ...enrichedAttributesRef.current.get(contactId),
        plainTextDob: { value: '' },
      });
      webApisClient
        .decryptAttribute(dobValue)
        .then((decryptedDob) => {
          // Ignore late results for a contact that already ended, otherwise we
          // strand an enriched entry and the panel won't hide on the next close.
          if (
            !getAgentContacts().some((c) => c.getContactId() === contactId)
          ) {
            return;
          }
          const formatted = { value: formatDate(decryptedDob) };
          enrichedAttributesRef.current.set(contactId, {
            ...enrichedAttributesRef.current.get(contactId),
            plainTextDob: formatted,
          });
          setAttributesMap((prev) => ({
            ...prev,
            plainTextDob: formatted,
          }));
        })
        .catch(() => console.error('Error decrypting DOB'));
    } else {
      const formatted = { value: formatDate(dobValue) };
      enrichedAttributesRef.current.set(contactId, {
        ...enrichedAttributesRef.current.get(contactId),
        plainTextDob: formatted,
      });
    }
  };

  /** Fetch Kentico survey content + case IDs if not already loaded. Safe to call multiple times. */
  const ensureSurveyContent = async (contactId, attributes) => {
    // Only fetch Kentico content if not already loaded
    if (Object.keys(surveyCalls).length === 0) {
      const kenticoContentResponse =
        await remoteConfigManager.getSurveyContent();
      setSurveyCalls(kenticoContentResponse.calls);
      setSurveyChats(kenticoContentResponse.chats);
      setSurveyCallbackTask(kenticoContentResponse.callback_task_outbound_call);
      setSurveyFailureTask(kenticoContentResponse.employee_portal_failure_task);
      setSurveyIntermittentAbsenceTask(
        kenticoContentResponse.intermittent_absence_task,
      );
      setSurveyRequestTypeOptions(
        kenticoContentResponse.request_type_options.options.value.split('|'),
      );
      setSurveyWorkItemTypeOptions(
        kenticoContentResponse.work_item_type_options.options.value.split('|'),
      );
      setSurveyCallerTypeOptions(
        kenticoContentResponse.caller_type.options.value.split('|'),
      );
    }

    // Only fetch case IDs if not already loaded for this contact
    const alreadyHasCaseIds = surveyCaseIdsRef.current.some?.(
      (c) => c.contactId === contactId,
    );
    if (!alreadyHasCaseIds && attributes) {
      // Determine which identifier attribute to use for case ID lookup
      let lookupAttr = null;
      if (attributes.customerId?.value) {
        lookupAttr = attributes.customerId;
      } else if (attributes.eid?.value) {
        lookupAttr = attributes.eid;
      }

      if (lookupAttr) {
        const returnedCaseIds = await remoteConfigManager.getCaseIds(lookupAttr);
        if (returnedCaseIds?.casedIds) {
          setSurveyCaseIds((prevCaseIds) =>
            prevCaseIds.some((c) => c.contactId === contactId)
              ? prevCaseIds
              : [
                  ...prevCaseIds,
                  {
                    contactId,
                    caseIds: ['N/A', 'Other', ...returnedCaseIds.casedIds],
                    mostRecent: returnedCaseIds.mostRecent || '',
                  },
                ]
          );
        }
      } else {
        setSurveyCaseIds(['N/A', 'Other']);
      }
    }
  };

  /** Merge raw Connect attributes with our enriched data (borderColor, plainTextDob, hasTasks) */
  const getEnrichedAttributes = (contactId, rawAttributes) => {
    const enriched = enrichedAttributesRef.current.get(contactId) || {};
    return {
      ...rawAttributes,
      contactId,
      typeId: enriched.typeId || rawAttributes.typeId,
      ...(enriched.borderColor && { borderColor: enriched.borderColor }),
      ...(enriched.plainTextDob && { plainTextDob: enriched.plainTextDob }),
      ...(enriched.hasTasks && { hasTasks: enriched.hasTasks }),
    };
  };

  /**
   * Build the contacts array shaped as InfoTab expects:
   * [{ attributes: { contactId, typeId, borderColor, firstName, lastName } }]
   */
  const buildContactsForInfoTab = () => {
    return getAgentContacts()
      .filter((c) => {
        try {
          if (isMonitoringContact(c)) return false;
          // Exclude terminal missed/rejected contacts (e.g. a missed task lingering after refresh).
          const status = c.getStatus().type;
          return (
            status !== connect.ContactStateType.MISSED &&
            status !== connect.ContactStateType.REJECTED &&
            status !== connect.ContactStateType.ERROR
          );
        } catch {
          return true;
        }
      })
      .reduce((acc, c) => {
        try {
          const contactId = c.getContactId();
          const attrs = c.getAttributes();
          const enriched = enrichedAttributesRef.current.get(contactId) || {};
          acc.push({
            attributes: {
              contactId,
              typeId: enriched.typeId || getInteractionType(c),
              borderColor: enriched.borderColor,
              firstName: attrs.firstName,
              lastName: attrs.lastName,
            },
          });
        } catch {
          // Skip contacts that crash during property access (e.g. callbacks with uninitialized connections)
        }
        return acc;
      }, []);
  };

  const getCallerType = (callerType) => {
    if (callerType === 'Family') {
      return 'Family Member/Partner';
    }

    return callerType;
  };

  const getRequestType = (attributes) => {
    if (!attributes.typeId || attributes.typeId === '') {
      return 'Service - Call';
    }
    if (attributes.typeId === 'Chat') {
      return attributes.chatTopic?.value === 'File a new claim or leave'
        ? 'Intake - Chat'
        : 'Service - Chat';
    }
    if (attributes.typeId === 'Task') {
      if (attributes.callReason?.value)
        return 'Service - Outbound Scheduled Callback';
      if (attributes.msgType?.value)
        return 'Service - Employee Portal Failure Task';
      return 'Service - Intermittent Absence Manual Handling Task';
    }
    return attributes.CallDisposition?.value?.endsWith('Intake')
      ? 'Intake - Call'
      : 'Service - Call';
  };

  const getLanguage = (attributes) => {
    if (attributes.Language?.value) {
      return attributes.Language.value === 'english' ? 'English' : 'Spanish';
    }
    return 'English';
  };

  const initSurveyResponses = (contactId, attributes) => {
    const caseIds = surveyCaseIdsRef.current;
    return {
      contactId,
      responses: {
        generalDetails: {
          firstName: userRef.current.firstName,
          lastName: userRef.current.lastName,
          email: userRef.current.email,
          customerNumber: attributes.customerNumber?.value || '',
          language: getLanguage(attributes),
          callerType: attributes.callerType?.value
            ? getCallerType(attributes.callerType.value)
            : '',
          requestType: getRequestType(attributes),
          claim:
            caseIds.length > 0
              ? caseIds.find((c) => c.contactId === contactId)?.mostRecent ?? ''
              : '',
          workItemType: '',
          firstTimeIssue: 'Unknown',
          liabilityDecisionReceived: 'Yes',
        },
        callReasons: [],
        noteReasons: [],
        primaryCallReason: '',
        taskDetails: {
          successfullyReached: 'Yes',
          callBackAttempts: 0,
          callBackNumber: attributes.numberToCall?.value || '',
          callBackTopic: attributes.callReason?.value || '',
        },
        additionalDetails: '',
        notes: '',
        addNotesList: [],
        inputList: [],
        selected: '',
        note: '',
      },
    };
  };

  const sendDesktopNotification = (contactName, contactId) => {
    const chatNotification = new Notification(
      `New Chat Message from ${contactName}`,
      {},
    );
    chatNotification.onclick = () => {
      window.parent.parent.focus();
      connect.core.viewContact(contactId);
    };
  };

  const handleChatMessageReceived = (event) => {
    const { data } = event;

    if (data.ParticipantRole === 'CUSTOMER') {
      if (
        document.visibilityState === 'hidden' ||
        data.ContactId !== activeContactRef.current
      ) {
        sendDesktopNotification(data.DisplayName, data.ContactId);
      }
    }
  };

  // --- Border Color Management ---

  function incrementBorderColorIndex() {
    if (borderColorIndexRef.current >= contactBorderColors.length - 1) {
      borderColorIndexRef.current = 0;
    } else {
      borderColorIndexRef.current++;
    }
  }

  function setNextBorderColorIndex() {
    incrementBorderColorIndex();

    // Check existing chat contacts for color conflicts
    const chatContacts = getAgentContacts().filter(
      (c) => c.getType() === connect.ContactType.CHAT,
    );
    if (chatContacts.length > 0) {
      const existingContactId = chatContacts[0].getContactId();
      const existingBorderColor =
        enrichedAttributesRef.current.get(existingContactId)?.borderColor;
      const nextBorderColor = contactBorderColors[borderColorIndexRef.current];
      if (existingBorderColor === nextBorderColor) {
        incrementBorderColorIndex();
      }
    }
  }

  // --- Contact Event Handlers ---

  const handleContactConnecting = async (contact) => {
    console.log('APEH CONTACT CONNECTING');

    // Skip monitoring contacts (supervisor listen/barge) — no InfoTab needed
    if (isMonitoringContact(contact)) return;

    const contactId = contact.getContactId();
    const attributes = contact.getAttributes();
    event('Contact Connecting', { contactId });

    await ensureSurveyContent(contactId, attributes);
  };

  const handleContactAccepted = async (contact) => {
    console.log('APEH CONTACT ACCEPTED');

    // Skip monitoring contacts (supervisor listen/barge) — no InfoTab needed
    if (isMonitoringContact(contact)) return;

    const contactId = contact.getContactId();
    event('Contact Accepted', { contactId });

    // Guard against duplicate onAccepted calls (e.g. React StrictMode double-fire).
    // Uses a separate Set so it doesn't conflict with enrichedAttributesRef
    // (which handleContactViewed may have already populated).
    if (acceptedContactIdsRef.current.has(contactId)) return;
    acceptedContactIdsRef.current.add(contactId);

    const contactType = contact.getType();
    const attributes = contact.getAttributes();
    const typeId = getInteractionType(contact);

    // Build enriched data for this contact
    const enriched = { typeId };

    if (contactType === connect.ContactType.CHAT) {
      const existing = enrichedAttributesRef.current.get(contactId);
      if (!existing?.borderColor) {
        enriched.borderColor = contactBorderColors[borderColorIndexRef.current];
        setNextBorderColorIndex();
      }
    }

    // Handle DOB decryption/formatting
    enrichDob(contactId, contactType, attributes.dob?.value);

    // Check for open tasks
    if (attributes.customerNumber && attributes.customerNumber.value !== '') {
      remoteConfigManager
        .hasTasks(attributes.customerNumber)
        .then((res) => {
          if (res.hasTasks === 'Yes') {
            // Ignore late results for a contact that already ended, otherwise we
            // strand an enriched entry and the panel won't hide on the next close.
            if (
              !getAgentContacts().some((c) => c.getContactId() === contactId)
            ) {
              return;
            }
            enrichedAttributesRef.current.set(contactId, {
              ...enrichedAttributesRef.current.get(contactId),
              hasTasks: { value: true },
            });
            setAttributesMap((prev) => ({
              ...prev,
              hasTasks: { value: true },
            }));
          }
        })
        .catch(() => console.log('Error checking for tasks'));
    }

    // Store enriched data
    enrichedAttributesRef.current.set(contactId, {
      ...enrichedAttributesRef.current.get(contactId),
      ...enriched,
    });

    // Build merged attributes for survey init and display
    const mergedAttributes = getEnrichedAttributes(contactId, attributes);

    const initialResponses = initSurveyResponses(contactId, mergedAttributes);
    // Replace any response created earlier by handleContactViewed to avoid duplicates
    setSurveyResponses((prevResponses) => [
      ...prevResponses.filter((r) => r.contactId !== contactId),
      initialResponses,
    ]);

    //Set Verint div to visible
    setShowVerintIdentifier(true);

    setInfoTabSelected(0);
    setAttributesMap(mergedAttributes);
    setActiveContactId(contactId);
    setHasContacts(true);

    if (contactType === connect.ContactType.CHAT) {
      const cnn = contact
        .getConnections()
        .find((cnn) => cnn.getType() === connect.ConnectionType.AGENT);

      const agentChatSession = await cnn.getMediaController();

      agentChatSession.onMessage(handleChatMessageReceived);
    }

    setCCPWidth(60);
    setShowInfoPanel(true);
  };

  /** @param {connect.Contact} contact */
  const handleSendDefaultGreeting = async (contact) => {
    // Only send default greeting for chats
    if (contact.getType() === connect.ContactType.CHAT) {
      console.log(`SEND DEFAULT GREETING-${contact.contactId}`);
      event('Send Default Greeting', { contactId: contact.contactId });

      const agentInfo = new connect.Agent().getConfiguration();
      const cnn = contact
        .getConnections()
        .find((cnn) => cnn.getType() === connect.ConnectionType.AGENT);
      const agentChatSession = await cnn.getMediaController();

      try {
        // The API call below will send an initial greeting to the customer. While the
        // onConnected event could execute multiple times per chat session (i.e. due to
        // browser refreshes, new tab loads, multiple browser tabs/instances/etc), the
        // API below will not send the same message more than once for the same ClientToken.
        await fetch(
          'https://participant.connect.us-east-1.amazonaws.com/participant/message',
          {
            headers: {
              'content-type': 'application/json',
              'x-amz-bearer':
                agentChatSession.controller.connectionHelper.getConnectionToken(),
            },
            method: 'POST',
            body: JSON.stringify({
              ClientToken: `${contact.contactId}-INITIAL_GREETING`,
              Content: `Hello! My name is ${agentInfo.firstName}, and I would be more than happy to assist you today. May I call you by your first name?`,
              ContentType: 'text/plain',
            }),
          },
        );
      } catch {
        console.error('Error sending default greeting');
      }
    }
  };

  /** @param {connect.Contact} contact */
  const handleContactConnected = async (contact) => {
    console.log('APEH CONTACT CONNECTED');
    event('Contact Connected', { contactId: contact.contactId });

    // Delay sending the default greeting for 3 seconds to allow chat topic/details
    // from the EE portal to process first, so the default greeting timestamp reliably
    // follows those messages in the chat transcript.
    setTimeout(handleSendDefaultGreeting, 3000, contact);
  };

  const handleAfterCallWork = (contact) => {
    event('Contact ACW', { contactId: contact.contactId });
    setInfoTabSelected(1);
  };

  const handleContactDestroyed = (contact) => {
    console.log('APEH CONTACT DESTROYED');
    event('Contact Destroyed', { contactId: contact.contactId });

    if (contact && contact.contactId !== '') {
      // Clean up enriched data for this contact
      enrichedAttributesRef.current.delete(contact.contactId);
      acceptedContactIdsRef.current.delete(contact.contactId);

      // Check both our local Map and the Connect API to determine if contacts remain.
      // The Map may be incomplete after a browser refresh (handleContactAccepted doesn't re-fire),
      // and getAgentContacts() can transiently return [] during network hiccups.
      // Only hide the panel when both agree there are no remaining contacts.
      const apiContacts = getAgentContacts().filter(
        (c) => c.getContactId() !== contact.contactId,
      );
      if (
        enrichedAttributesRef.current.size === 0 &&
        apiContacts.length === 0
      ) {
        setActiveContactId('');
        setShowInfoPanel(false);
        setHasContacts(false);
        setAttributesMap({});
        setCCPWidth(95);
        setShowVerintIdentifier(false);
      } else if (
        activeContactRef.current === contact.contactId &&
        apiContacts.length > 0
      ) {
        // The actively-viewed contact was destroyed; switch to a remaining
        // contact so the InfoTab doesn't keep showing the ended contact's data.
        const next = apiContacts[0];
        const nextId = next.getContactId();
        const nextExisting = enrichedAttributesRef.current.get(nextId);
        if (!nextExisting?.typeId) {
          enrichedAttributesRef.current.set(nextId, {
            ...nextExisting,
            typeId: getInteractionType(next),
          });
        }
        const nextAttrs = next.getAttributes();
        enrichDob(nextId, next.getType(), nextAttrs.dob?.value);
        setActiveContactId(nextId);
        setAttributesMap(getEnrichedAttributes(nextId, nextAttrs));
        setInfoTabSelected(0);
      }
    }
  };

  const handleContactViewed = (viewEvent) => {
    console.log('APEH CONTACT VIEWED');
    event('Contact Viewed', { contactId: viewEvent.contactId });

    if (viewEvent.contactId !== '') {
      const agentContacts = getAgentContacts();
      const contact = agentContacts.find(
        (c) => c.getContactId() === viewEvent.contactId,
      );

      if (!contact) {
        console.log('Contact not found for viewEvent:', viewEvent.contactId);
        return;
      }

      // Ignore contacts in a terminal missed/rejected state (e.g. a missed task
      // that lingers in the snapshot after refresh) — they must not show the panel.
      const viewedStatus = contact.getStatus().type;
      if (
        viewedStatus === connect.ContactStateType.MISSED ||
        viewedStatus === connect.ContactStateType.REJECTED ||
        viewedStatus === connect.ContactStateType.ERROR
      ) {
        return;
      }

      // Assign border colors for ALL chat contacts missing one (e.g. after browser refresh).
      // handleContactViewed only fires for the viewed contact, so we must cover all chats here.
      agentContacts.forEach((c) => {
        const cId = c.getContactId();
        if (c.getType() === connect.ContactType.CHAT) {
          const existing = enrichedAttributesRef.current.get(cId);
          if (!existing?.borderColor) {
            enrichedAttributesRef.current.set(cId, {
              ...existing,
              borderColor: contactBorderColors[borderColorIndexRef.current],
            });
            setNextBorderColorIndex();
          }
        }
      });

      const contactId = contact.getContactId();
      const contactType = contact.getType();
      const attributes = contact.getAttributes();

      // Ensure typeId is set (e.g. after browser refresh, handleContactAccepted doesn't re-fire)
      const existingEnriched = enrichedAttributesRef.current.get(contactId);
      if (!existingEnriched?.typeId) {
        enrichedAttributesRef.current.set(contactId, {
          ...existingEnriched,
          typeId: getInteractionType(contact),
        });
      }

      // Re-enrich DOB if needed (e.g. after browser refresh)
      enrichDob(contactId, contactType, attributes.dob?.value);

      const mergedAttributes = getEnrichedAttributes(contactId, attributes);

      // Initialize survey responses if missing (e.g. after browser refresh)
      setSurveyResponses((prev) => {
        const exists = prev.some((r) => r.contactId === contactId);
        if (!exists) {
          const initialResponses = initSurveyResponses(
            contactId,
            mergedAttributes,
          );
          return [...prev, initialResponses];
        }
        return prev;
      });

      // Show the panel immediately if not connecting. Also update when the
      // previously-active contact no longer exists (was destroyed), so we don't
      // strand the InfoTab on an ended contact while this one is still connecting.
      const isConnecting =
        contact.getStatus().type === connect.ContactStateType.CONNECTING;
      const activeWasDestroyed =
        activeContactRef.current !== '' &&
        !agentContacts.some(
          (c) => c.getContactId() === activeContactRef.current,
        );
      if (!isConnecting || activeWasDestroyed) {
        setActiveContactId(contactId);
        setAttributesMap(mergedAttributes);
        setHasContacts(true);
        setCCPWidth(60);
        setShowInfoPanel(true);
        setInfoTabSelected(0);
      }

      // Fetch survey content/case IDs in background (e.g. after browser refresh)
      ensureSurveyContent(contactId, attributes);
    }
  };

  useEffect(() => {
    console.log('INSIDE USE EFFECT');

    // FullStory Identify API
    const displayName =
      userRef.current.firstName + ' ' + userRef.current.lastName;
    identify(userRef.current.employeeId, { displayName });

    if ('Notification' in globalThis) Notification.requestPermission();

    if (!ccpInitialized) {
      setCcpInitialized(true);
      const url =
        'https://myapps.microsoft.com/signin/' + envConfig.azureTenant;
      const ccpUrl = envConfig.ccpUrl;

      globalThis['_fs_run_in_iframe'] = true;
      const ccpContainer = document.getElementById('ccp');

      // Initialize the CCP window
      connect.core.initCCP(ccpContainer, {
        ccpUrl,
        loginPopup: true,
        loginUrl: url,
        loginPopupAutoClose: true,
        softphone: { allowFramedSoftphone: true },
        region: 'us-east-1', // REQUIRED for `CHAT`, optional otherwise
      });

      connect.core.onAuthorizeSuccess(() => {
        console.log('authorization succeeded!');
        setLoggedIn(true);
      });

      const eventBus = connect.core.getEventBus();
      eventBus.subscribe(connect.EventType.TERMINATED, () => {
        console.log('Logged out');
        setLoggedIn(false);
        setCcpInitialized(false);
      });

      //Subscribe to contact events so we can handle them
      connect.contact(function (contact) {
        contact.onConnecting(handleContactConnecting);
        contact.onAccepted(handleContactAccepted);
        contact.onConnected(handleContactConnected);
        contact.onACW(handleAfterCallWork);
        contact.onDestroy(handleContactDestroyed);
      });

      connect.core.onViewContact(handleContactViewed);
    }
  }, []);

  // Called from infoTab to set the contact as the active one in the CCP
  // when an infoTab contact is clicked
  const handleInfoTabContactViewed = (contactId) => {
    connect.core.viewContact(contactId);
  };

  return (
    <div
      id="parent"
      style={{
        height: '98vh',
        width: '100vw',
        margin: '0',
        backgroundColor: '#F6F8FA',
        overflow: 'hidden',
      }}
    >
      {loggedIn ? (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <CCPContainer width={ccpWidth} contactAttributes={attributesMap} />
          {hasContacts ? (
            <div
              className={`info-tab-container ${
                showInfoPanel ? 'info-tab-visible' : ''
              }`}
            >
              <InfoTab
                borderColor={contactBorderColors[borderColorIndexRef.current]}
                callbackTask={surveyCallbackTask}
                callerTypeOptions={surveyCallerTypeOptions}
                calls={surveyCalls}
                caseIds={surveyCaseIds}
                chats={surveyChats}
                contactAttributes={attributesMap}
                contacts={buildContactsForInfoTab()}
                failureTask={surveyFailureTask}
                handleInfoTabContactViewedFunction={handleInfoTabContactViewed}
                infoTabSelected={infoTabSelected}
                intermittentAbsenceTask={surveyIntermittentAbsenceTask}
                requestTypeOptions={surveyRequestTypeOptions}
                setInfoTabSelected={setInfoTabSelected}
                setSurveyResponses={setSurveyResponses}
                showVerintIdentifier={showVerintIdentifier}
                surveyResponses={surveyResponses}
                user={userRef.current}
                workItemTypeOptions={surveyWorkItemTypeOptions}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <NotLoggedIn />
      )}
    </div>
  );
};

export default ParentContainerV2;
